import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const GardenContainer = styled(motion.section)`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(255, 105, 180, 0.2);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(90deg, #ff1493, #ff69b4, #da70d6);
  }
`;

const GardenTitle = styled(motion.h3)`
  font-size: 1.8rem;
  color: #d84079;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
`;

const GardenDescription = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 1.2rem;
  text-align: right;
  color: #555;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
`;

const GardenCanvas = styled.div`
  width: 100%;
  height: 400px;
  background: linear-gradient(180deg, #b3e5fc 0%, #e1f5fe 60%, #b3e5fc 100%);
  border-radius: 15px;
  position: relative;
  overflow: hidden;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="%23d84079"><path d="M15 5l-1-2H5L4 5H1v2h18V5zM5 21h14l1-10H4z"/></svg>') 12 12, auto;
  user-select: none;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.3) 20px, transparent 20px),
      radial-gradient(circle at 75% 55%, rgba(255, 255, 255, 0.3) 25px, transparent 25px),
      radial-gradient(circle at 40% 70%, rgba(255, 255, 255, 0.3) 15px, transparent 15px);
    z-index: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 80px;
    background: linear-gradient(to top, #5d4037 0%, #8d6e63 30%, #a1887f 70%, transparent 100%);
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
  }
`;

const SkyElement = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
`;

const Cloud = styled(motion.div)`
  position: absolute;
  background: white;
  border-radius: 50%;
  filter: blur(10px);
  opacity: 0.7;
`;

const GrassLayer = styled.div`
  position: absolute;
  bottom: 80px;
  left: 0;
  width: 100%;
  height: 40px;
  background-image: 
    linear-gradient(90deg, transparent 0%, rgba(76, 175, 80, 0.2) 30%, rgba(76, 175, 80, 0.2) 70%, transparent 100%),
    repeating-linear-gradient(
      60deg,
      transparent 0px,
      transparent 3px,
      rgba(76, 175, 80, 0.6) 3px,
      rgba(76, 175, 80, 0.6) 6px
    );
  z-index: 2;
`;

const GrassStrand = styled(motion.div)`
  position: absolute;
  bottom: 80px;
  width: 3px;
  height: ${props => props.height || '20px'};
  background: linear-gradient(to top, #2e7d32, #4caf50);
  transform-origin: bottom center;
  z-index: 2;
`;

const Butterfly = styled(motion.div)`
  position: absolute;
  width: 20px;
  height: 20px;
  z-index: 5;
  pointer-events: none;
`;

const FlowerControls = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 1.5rem;
`;

const FlowerButton = styled(motion.button)`
  background: ${props => props.color || '#ff69b4'};
  color: white;
  border: none;
  padding: 0.7rem 1.2rem;
  border-radius: 50px;
  font-family: 'Vazirmatn', sans-serif;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.25);
  }
`;

const FlowerIcon = styled.span`
  font-size: 1.2rem;
`;

const FlowerElement = styled(motion.div)`
  position: absolute;
  transform-origin: bottom center;
  z-index: ${props => props.zIndex || 1};
  
  svg {
    filter: drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.2));
  }
`;

const ClearButton = styled(motion.button)`
  background: linear-gradient(45deg, #f44336, #e91e63);
  color: white;
  border: none;
  padding: 0.7rem 1.2rem;
  border-radius: 50px;
  font-family: 'Vazirmatn', sans-serif;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  box-shadow: 0 4px 15px rgba(244, 67, 54, 0.3);
`;

const LoveMessage = styled(motion.div)`
  position: absolute;
  font-size: 0.8rem;
  color: #d84079;
  font-weight: 500;
  text-shadow: 0px 1px 2px rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
`;

const getRandomText = () => {
  const texts = [
    "دوستت دارم دلآرام",
    "زیباترینِ من",
    "عشق من",
    "فرشته‌ی زندگیم",
    "با تو خوشبختم",
    "قلب من مال توست",
    "ملکه‌ی قلبم",
    "روز و شبم تویی",
    "برای همیشه عاشقتم",
    "نفسم به نفست بنده"
  ];
  return texts[Math.floor(Math.random() * texts.length)];
};

// Enhanced SVG flower components with better shadows and details
const RoseFlower = ({ color = "#ff6b81" }) => (
  <svg width="60" height="120" viewBox="0 0 60 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="shadow" x="0" y="0" width="150%" height="150%">
        <feDropShadow dx="1" dy="3" stdDeviation="2" flood-opacity="0.3" />
      </filter>
      <radialGradient id="roseGradient" cx="30" cy="30" r="20" gradientUnits="userSpaceOnUse">
        <stop offset="10%" stop-color="#fff" stop-opacity="0.4" />
        <stop offset="90%" stop-color={color} />
      </radialGradient>
    </defs>
    <path d="M30 60L30 120" stroke="#2e7d32" strokeWidth="3" />
    <path d="M30 60L40 50" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 70L20 65" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 80L45 85" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 90L15 95" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 15C30 23.2843 23.2843 34 15 34C23.2843 34 30 44.7157 30 53C30 44.7157 36.7157 34 45 34C36.7157 34 30 23.2843 30 15Z" fill="url(#roseGradient)" filter="url(#shadow)" />
    <path d="M30 25C30 33.2843 23.2843 44 15 44C23.2843 44 30 54.7157 30 63C30 54.7157 36.7157 44 45 44C36.7157 44 30 33.2843 30 25Z" fill="url(#roseGradient)" filter="url(#shadow)" />
  </svg>
);

const TulipFlower = ({ color = "#e040fb" }) => (
  <svg width="60" height="120" viewBox="0 0 60 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="shadow" x="0" y="0" width="150%" height="150%">
        <feDropShadow dx="1" dy="3" stdDeviation="2" flood-opacity="0.3" />
      </filter>
      <linearGradient id="tulipGradient" x1="15" y1="15" x2="45" y2="50" gradientUnits="userSpaceOnUse">
        <stop offset="10%" stop-color="#fff" stop-opacity="0.5" />
        <stop offset="90%" stop-color={color} />
      </linearGradient>
    </defs>
    <path d="M30 60L30 120" stroke="#2e7d32" strokeWidth="3" />
    <path d="M30 75L40 65" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 90L20 80" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 60C30 60 15 40 15 25C15 15 25 5 30 15C35 5 45 15 45 25C45 40 30 60 30 60Z" fill="url(#tulipGradient)" filter="url(#shadow)" />
  </svg>
);

const DaisyFlower = ({ color = "#ffeb3b" }) => (
  <svg width="60" height="120" viewBox="0 0 60 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M30 50L30 120" stroke="#2e7d32" strokeWidth="3" />
    <path d="M30 70L40 60" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 85L20 75" stroke="#2e7d32" strokeWidth="2" />
    <circle cx="30" cy="30" r="15" fill={color} />
    <circle cx="30" cy="30" r="7" fill="#ffa000" />
    <path d="M30 5L30 15" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <path d="M30 45L30 55" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <path d="M55 30L45 30" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <path d="M15 30L5 30" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <path d="M47 13L40 20" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <path d="M20 40L13 47" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <path d="M47 47L40 40" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <path d="M20 20L13 13" stroke={color} strokeWidth="5" strokeLinecap="round" />
  </svg>
);

const SunflowerFlower = ({ color = "#ffc107" }) => (
  <svg width="60" height="120" viewBox="0 0 60 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M30 60L30 120" stroke="#2e7d32" strokeWidth="3" />
    <path d="M30 80L40 70" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 100L20 90" stroke="#2e7d32" strokeWidth="2" />
    <circle cx="30" cy="30" r="15" fill={color} />
    <circle cx="30" cy="30" r="8" fill="#5d4037" />
    <path d="M30 5L30 15" stroke={color} strokeWidth="6" strokeLinecap="round" />
    <path d="M30 45L30 55" stroke={color} strokeWidth="6" strokeLinecap="round" />
    <path d="M55 30L45 30" stroke={color} strokeWidth="6" strokeLinecap="round" />
    <path d="M15 30L5 30" stroke={color} strokeWidth="6" strokeLinecap="round" />
    <path d="M47 13L40 20" stroke={color} strokeWidth="6" strokeLinecap="round" />
    <path d="M20 40L13 47" stroke={color} strokeWidth="6" strokeLinecap="round" />
    <path d="M47 47L40 40" stroke={color} strokeWidth="6" strokeLinecap="round" />
    <path d="M20 20L13 13" stroke={color} strokeWidth="6" strokeLinecap="round" />
  </svg>
);

const OrchidFlower = ({ color = "#ba68c8" }) => (
  <svg width="60" height="120" viewBox="0 0 60 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M30 60L30 120" stroke="#2e7d32" strokeWidth="3" />
    <path d="M30 70L40 65" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 80L20 75" stroke="#2e7d32" strokeWidth="2" />
    <path d="M15 20C15 20 25 15 30 25C35 15 45 20 45 20C45 20 40 35 30 40C20 35 15 20 15 20Z" fill={color} />
    <path d="M20 40C20 40 25 35 30 45C35 35 40 40 40 40C40 40 35 55 30 60C25 55 20 40 20 40Z" fill={color} />
    <path d="M30 25L30 45" stroke="#2e7d32" strokeWidth="2" />
  </svg>
);

const LilyFlower = ({ color = "#ff4081" }) => (
  <svg width="60" height="120" viewBox="0 0 60 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M30 50L30 120" stroke="#2e7d32" strokeWidth="3" />
    <path d="M30 65L40 60" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 80L20 75" stroke="#2e7d32" strokeWidth="2" />
    <path d="M30 20L20 10L30 15L40 5L35 20L50 25L35 30L40 45L30 35L20 50L25 30L10 25L30 20Z" fill={color} />
  </svg>
);

// SVG for butterfly
const ButterflyIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C8 2 6 5 6 8C6 9.5 7 10.5 8 11C6 11.5 4 13 4 16C4 19 7 22 12 22C17 22 20 19 20 16C20 13 18 11.5 16 11C17 10.5 18 9.5 18 8C18 5 16 2 12 2Z" fill={color} opacity="0.8" />
    <path d="M12 8L10 13L12 18L14 13L12 8Z" fill="black" opacity="0.5" />
  </svg>
);

const FlowerGarden = () => {
  const [flowers, setFlowers] = useState([]);
  const [selectedFlower, setSelectedFlower] = useState('rose');
  const [messages, setMessages] = useState([]);
  const [grassStrands, setGrassStrands] = useState([]);
  const [butterflies, setButterflies] = useState([]);
  const gardenRef = useRef(null);
  
  const flowerTypes = {
    rose: { component: RoseFlower, color: '#ff6b81', name: 'رز' },
    tulip: { component: TulipFlower, color: '#e040fb', name: 'لاله' },
    daisy: { component: DaisyFlower, color: '#ffeb3b', name: 'مینا' },
    sunflower: { component: SunflowerFlower, color: '#ffc107', name: 'آفتابگردان' },
    orchid: { component: OrchidFlower, color: '#ba68c8', name: 'ارکیده' },
    lily: { component: LilyFlower, color: '#ff4081', name: 'سوسن' }
  };
  
  useEffect(() => {
    // Generate grass strands
    if (gardenRef.current) {
      const rect = gardenRef.current.getBoundingClientRect();
      const newGrassStrands = [];
      
      for (let i = 0; i < 60; i++) {
        newGrassStrands.push({
          id: i,
          left: Math.random() * rect.width,
          height: 15 + Math.random() * 25,
          delay: Math.random() * 2
        });
      }
      
      setGrassStrands(newGrassStrands);
      
      // Generate a few butterflies
      const newButterflies = [];
      const butterflyColors = ['#f06292', '#ba68c8', '#64b5f6', '#4db6ac'];
      
      for (let i = 0; i < 3; i++) {
        newButterflies.push({
          id: i,
          x: Math.random() * (rect.width - 50) + 25,
          y: Math.random() * (rect.height - 150) + 50,
          color: butterflyColors[Math.floor(Math.random() * butterflyColors.length)],
          duration: 15 + Math.random() * 20
        });
      }
      
      setButterflies(newButterflies);
    }
  }, []);
  
  const addFlower = (e) => {
    if (!gardenRef.current) return;
    
    const rect = gardenRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Don't plant if too close to the bottom (soil area)
    if (y > rect.height - 85) return;
    
    const newFlower = {
      id: Date.now(),
      type: selectedFlower,
      x,
      y,
      scale: 0,
      rotation: Math.random() * 10 - 5,
      zIndex: Math.floor(y)
    };
    
    setFlowers(prev => [...prev, newFlower]);
    
    // Add a love message
    const newMessage = {
      id: Date.now(),
      text: getRandomText(),
      x: x - 30,
      y: y - 30,
      opacity: 0
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    // Remove message after animation
    setTimeout(() => {
      setMessages(prev => prev.filter(m => m.id !== newMessage.id));
    }, 2000);
  };
  
  const clearGarden = () => {
    setFlowers([]);
  };
  
  return (
    <GardenContainer
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, amount: 0.2 }}
    >
      <GardenTitle
        animate={{
          scale: [1, 1.03, 1]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      >
        باغچه‌ی عشق برای دلآرام
      </GardenTitle>
      
      <GardenDescription
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        viewport={{ once: true }}
      >
        برای دلآرام عزیزم، با هر کلیک یک گل در باغچه‌ی عشقمان بکار. هر گل نمادی از عشق بی‌پایان من به توست. همانطور که این باغچه با گل‌ها پر می‌شود، قلب من نیز با عشق تو لبریز می‌شود.
      </GardenDescription>
      
      <GardenCanvas 
        ref={gardenRef} 
        onClick={addFlower}
      >
        <SkyElement>
          {/* Clouds */}
          <Cloud 
            style={{ top: '15%', left: '10%', width: '80px', height: '40px' }}
            animate={{
              x: [0, 30, 0],
              opacity: [0.7, 0.8, 0.7]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <Cloud 
            style={{ top: '30%', right: '15%', width: '120px', height: '50px' }}
            animate={{
              x: [0, -40, 0],
              opacity: [0.6, 0.7, 0.6]
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <Cloud 
            style={{ top: '10%', right: '30%', width: '60px', height: '30px' }}
            animate={{
              x: [0, 20, 0],
              opacity: [0.5, 0.6, 0.5]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </SkyElement>
        
        <GrassLayer />
        
        {grassStrands.map(grass => (
          <GrassStrand
            key={grass.id}
            style={{ 
              left: `${grass.left}px`,
              height: `${grass.height}px`
            }}
            animate={{
              rotateZ: [0, 5, -5, 0]
            }}
            transition={{
              duration: 2,
              delay: grass.delay,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          />
        ))}
        
        {butterflies.map(butterfly => (
          <Butterfly
            key={butterfly.id}
            style={{ left: butterfly.x, top: butterfly.y }}
            animate={{
              x: [0, 40, -30, 20, -40, 0],
              y: [0, -20, -40, -10, -30, 0],
              rotateY: [0, 180, 0, 180, 0],
              scale: [1, 1.1, 0.9, 1.1, 1]
            }}
            transition={{
              duration: butterfly.duration,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            <ButterflyIcon color={butterfly.color} />
          </Butterfly>
        ))}

        <AnimatePresence>
          {flowers.map((flower) => {
            const FlowerComponent = flowerTypes[flower.type].component;
            return (
              <FlowerElement
                key={flower.id}
                initial={{ scale: 0, y: 20, opacity: 0 }}
                animate={{ 
                  scale: 1, 
                  y: 0, 
                  opacity: 1,
                  rotate: flower.rotation
                }}
                transition={{ 
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                  duration: 1
                }}
                style={{ 
                  left: `${flower.x - 30}px`, 
                  top: `${flower.y - 60}px`,
                  zIndex: flower.zIndex
                }}
                whileHover={{ scale: 1.05 }}
              >
                <FlowerComponent color={flowerTypes[flower.type].color} />
              </FlowerElement>
            );
          })}
          
          {messages.map((message) => (
            <LoveMessage
              key={message.id}
              initial={{ y: 0, opacity: 0 }}
              animate={{ y: -40, opacity: [0, 1, 0] }}
              transition={{ duration: 2 }}
              style={{ left: message.x, top: message.y }}
            >
              {message.text}
            </LoveMessage>
          ))}
        </AnimatePresence>
      </GardenCanvas>
      
      <FlowerControls>
        {Object.entries(flowerTypes).map(([type, { color, name }]) => (
          <FlowerButton
            key={type}
            color={color}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSelectedFlower(type)}
            style={{
              boxShadow: selectedFlower === type 
                ? `0 0 0 3px white, 0 0 0 6px ${color}` 
                : `0 4px 10px rgba(0, 0, 0, 0.2)`
            }}
          >
            <FlowerIcon>🌸</FlowerIcon>
            {name}
          </FlowerButton>
        ))}
      </FlowerControls>
      
      <div style={{ textAlign: 'center', marginTop: '15px' }}>
        <ClearButton
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={clearGarden}
        >
          پاک کردن باغچه
        </ClearButton>
      </div>
    </GardenContainer>
  );
};

export default FlowerGarden; 